import React, { ReactElement } from "react";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import { Translate } from "react-redux-i18n";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

import FontAwesomeIcon from "components/shared/FontAwesomeIcon";
import { logoutRequested } from "redux/auth/authSlice";
import { openDialog } from "redux/dialog/dialogSlice";
import { checkWalletPermissions } from "services/utils";
import styles from "./styles.module.scss";
import { toggleDesktopLayout } from "redux/layoutToggle/layoutToggleSlice";

interface MenuProps {
  menu: boolean;
  menuAnchor: (EventTarget & HTMLDivElement) | null;
  handleMenuClose: () => void;
  setLiveChatOpen: (state: boolean) => void;
}
function AccountMenu({
  menu,
  menuAnchor,
  handleMenuClose,
  setLiveChatOpen
}: MenuProps): ReactElement {
  const dispatch = useAppDispatch();
  const permissionsOfAuthUser = useAppSelector((state) => state.auth.permissions);
  const isPayment = useAppSelector((state) => state.config.skin?.setting.payment);
  const isRegistration = useAppSelector((state) => state.config.skin?.setting.registration)
  const livechatSetting = useAppSelector(state => state.config.skin?.setting.livechat)
  const hasGameBetasistPermission = permissionsOfAuthUser?.some((p) => p === "game.betassist");
  const isDesktopLayout = useAppSelector(state => state.layoutToggle.isDesktopLayout)

  const handlePrintOdd = () => {
    handleMenuClose()
    dispatch(openDialog({ type: 'printOdds' }));
  };
  const handleCreateUser = () => {
    handleMenuClose()
    dispatch(openDialog({ type: 'createUser' }));
  };
  const handleAccountTransfer = () => {
    handleMenuClose()
    dispatch(openDialog({ type: 'accountTransfer' }));
  };
  const handleLogout = () => {
    dispatch(toggleDesktopLayout({
      status: false,
      caller: !isDesktopLayout ? "mobile" : undefined
    }))
    dispatch(logoutRequested());
  };

  return (
    <Menu
      id="account-menu"
      anchorEl={menuAnchor}
      open={menu}
      onClose={handleMenuClose}
      disableAutoFocusItem
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      PaperProps={isDesktopLayout ? { className: styles.paper } : {}}
      className={styles.profileAccountMenu}
    >
      <MenuItem
        component={Link}
        to="/profile"
        className={styles.box}
        onClick={handleMenuClose}
      >
        <Box className={styles.menuImage}>
          <FontAwesomeIcon type="fal" name="user" margin={1} />
        </Box>
        <Translate value="nav.profile" />
      </MenuItem>
      {permissionsOfAuthUser &&
        checkWalletPermissions(permissionsOfAuthUser, "wallet.network") && (
          <MenuItem
            component={Link}
            to="/network/manage"
            onClick={handleMenuClose}
            className={styles.menuLink}
          >
            <Box className={styles.menuImage}>
              <FontAwesomeIcon type="fal" name="sitemap" margin={1} />
            </Box>
            <Translate value="nav.networkManage" />
          </MenuItem>
        )}
      <MenuItem
        component={Link}
        to="/reports/transactions"
        onClick={handleMenuClose}
        className={styles.menuLink}
      >
        <Box className={styles.menuImage}>
          <FontAwesomeIcon type="fal" name="clipboard-list" margin={1} />
        </Box>
        <Translate value="nav.trxList" />
      </MenuItem>
      <MenuItem
        component={Link}
        to="/reports/betting"
        onClick={handleMenuClose}
        className={styles.menuLink}
      >
        <Box className={styles.menuImage}>
          <FontAwesomeIcon type="fal" name="ballot-check" margin={1} />
        </Box>
        <Translate value="nav.betting" />
      </MenuItem>
      {
        permissionsOfAuthUser &&
        checkWalletPermissions(permissionsOfAuthUser, "wallet.network") &&
        <MenuItem
          onClick={handleAccountTransfer}
          className={styles.box}
        >
          <Box className={styles.menuImage}>
            <FontAwesomeIcon type="fal" name="money-bill-transfer" margin={1} />
          </Box>
          <Translate value="tooltip.transfer" />
        </MenuItem>
      }
      {
        permissionsOfAuthUser &&
        checkWalletPermissions(permissionsOfAuthUser, "wallet.network") &&
        <MenuItem
          onClick={handleCreateUser}
          className={styles.box}
        >
          <Box className={styles.menuImage}>
            <FontAwesomeIcon type="fal" name="wallet" margin={1} />
          </Box>
          <Translate value="buttons.createuser" />
        </MenuItem>
      }
      {permissionsOfAuthUser &&
        checkWalletPermissions(permissionsOfAuthUser, "wallet.provisional_plan") &&
        <MenuItem
          component={Link}
          to="/wallet/revenues"
          onClick={handleMenuClose}
          className={styles.menuLink}
        >
          <Box className={styles.menuImage}>
            <FontAwesomeIcon type="fal" name="money-bill-trend-up" margin={1} />
          </Box>
          <Translate value="nav.revenue" />
        </MenuItem>
      }
      {
        permissionsOfAuthUser &&
        checkWalletPermissions(permissionsOfAuthUser, "wallet.network") &&
        hasGameBetasistPermission &&
        <MenuItem
          onClick={handlePrintOdd}
          className={styles.box}
        >
          <Box className={styles.menuImage}>
            <FontAwesomeIcon type="fal" name="print" />
          </Box>
          <Translate value="nav.print" />
        </MenuItem>
      }

      {isPayment === 1 &&
        permissionsOfAuthUser &&
        checkWalletPermissions(permissionsOfAuthUser, "wallet.recharge") &&
        <MenuItem
          component={Link}
          to="/payments"
          onClick={handleMenuClose}
          className={styles.menuLink}
        >
          <Box className={styles.menuImage}>
            <FontAwesomeIcon type="fal" name="battery-half" margin={1} />
          </Box>
          <Translate value="buttons.recharge" />
        </MenuItem>
      }
      {process.env.REACT_APP_CHAT === "true" && isRegistration === 0 && livechatSetting === 1 &&
        <MenuItem
          onClick={() => {
            setLiveChatOpen(true)
            handleMenuClose()
          }}
          className={styles.menuLink}
        >
          <Box className={styles.menuImage}>
            <FontAwesomeIcon type="fal" name="comment-dots" margin={1} />
          </Box>
          LiveChat
        </MenuItem>}
      <MenuItem
        onClick={() => {
          handleMenuClose();
          handleLogout();
        }}
        className={styles.menuItemNoBorder}
      >
        <Box className={styles.menuImage}>
          <FontAwesomeIcon type="fal" name="sign-out" margin={1} />
        </Box>
        <Translate value="buttons.logout" />
      </MenuItem>

    </Menu >
  );
}

export default AccountMenu;